import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { OrpUserProfile } from '@/components/shared/api/endpoints/slack';
import { useUpsertOrpUserProfileMutation } from '@/components/shared/api/mutations/applicant';
import { useOrpUserProfilesQuery } from '@/components/shared/api/queries/applicant';
import { getAccessToken } from '@/components/shared/utils/sessionStorageUtils';

interface OrpUserProfilesContextValue {
    activeProfiles: OrpUserProfile[];
    isLoading: boolean;
}

const OrpUserProfilesContext = createContext<OrpUserProfilesContextValue>({
    activeProfiles: [],
    isLoading: false,
});

export const OrpUserProfilesProvider = ({ children }: { children: ReactNode }) => {
    const authEmail = useCurrentUserEmail();
    const hasAccessToken = Boolean(getAccessToken());

    const hasUpserted = useRef(false);
    const { mutate: upsertProfile } = useUpsertOrpUserProfileMutation();

    const { data: profiles, isLoading } = useOrpUserProfilesQuery(hasAccessToken);

    useEffect(() => {
        if (!hasAccessToken || !authEmail || hasUpserted.current) return;
        hasUpserted.current = true;
        upsertProfile(authEmail);
    }, [hasAccessToken, authEmail, upsertProfile]);

    const activeProfiles = useMemo(
        () => (profiles ?? []).filter((profile) => profile.isActive),
        [profiles],
    );

    const value = useMemo(() => ({ activeProfiles, isLoading }), [activeProfiles, isLoading]);

    return (
        <OrpUserProfilesContext.Provider value={value}>{children}</OrpUserProfilesContext.Provider>
    );
};

export const useOrpUserProfiles = () => useContext(OrpUserProfilesContext);
